import React, { useEffect, useState } from 'react';
import { Plus, Save, Trash2, Edit3, ArrowUp, ArrowDown, Eye, EyeOff, ExternalLink, X, Type, Image as ImageIcon, Film, Code2, Megaphone, Images, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../api/client';
import UploadField from './UploadField';

const BLOCK_TYPES = [
  { type: 'text', label: 'Testo', icon: Type, defaults: { content: 'Scrivi qui il tuo testo. Usa **grassetto**, *corsivo* e [link](https://...).' } },
  { type: 'image', label: 'Immagine', icon: ImageIcon, defaults: { src: '', alt: '', caption: '' } },
  { type: 'video', label: 'Video', icon: Film, defaults: { src: '', caption: '' } },
  { type: 'iframe', label: 'Iframe / Embed', icon: Code2, defaults: { src: '', height: 480 } },
  { type: 'banner', label: 'Banner CTA', icon: Megaphone, defaults: { title: 'Titolo banner', subtitle: '', bg: '', cta_label: '', cta_url: '' } },
  { type: 'gallery', label: 'Galleria', icon: Images, defaults: { items: [] } },
];

function uid() { return 'b' + Math.random().toString(36).slice(2, 9); }

export default function PageBuilder() {
  const [pages, setPages] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);

  const reload = async () => {
    setLoading(true);
    try { setPages(await api.listPages()); } catch (e) { toast.error('Errore nel caricamento pagine'); }
    setLoading(false);
  };

  useEffect(() => { reload(); }, []);

  const startNew = () => setEditing({
    title: 'Nuova pagina', subtitle: '', slug: '', published: true, show_in_menu: false, sort_order: pages.length, blocks: [],
  });

  const save = async (p) => {
    try {
      if (p.id) {
        await api.updatePage(p);
        toast.success('Pagina aggiornata');
      } else {
        const res = await api.createPage(p);
        toast.success('Pagina creata');
        p.id = res.id; p.slug = res.slug;
      }
      setEditing(null);
      reload();
    } catch (e) {
      toast.error(e?.message || 'Errore di salvataggio');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Eliminare definitivamente la pagina?')) return;
    try { await api.deletePage(id); toast.success('Pagina eliminata'); reload(); }
    catch (e) { toast.error(e?.message || 'Errore eliminazione'); }
  };

  if (editing) {
    return <PageEditor page={editing} onSave={save} onCancel={() => setEditing(null)} />;
  }

  return (
    <div className="space-y-4" data-testid="page-builder-list">
      <div className="flex items-center justify-between">
        <p className="text-white/70 text-sm">Crea pagine dinamiche con blocchi (testo, immagini, video, iframe, banner, gallery). Le pagine pubblicate sono accessibili a <code className="text-pink-brand">/p/&lt;slug&gt;</code>.</p>
        <button onClick={startNew} className="btn-primary !py-2 !px-4 !text-sm" data-testid="page-builder-new-button">
          <Plus size={14} /> Nuova pagina
        </button>
      </div>

      {loading ? (
        <div className="text-center py-10"><Loader2 className="text-pink-brand animate-spin mx-auto" /></div>
      ) : pages.length === 0 ? (
        <div className="glass-card p-8 text-center text-white/60 text-sm">Nessuna pagina creata. Clicca "Nuova pagina" per iniziare.</div>
      ) : (
        <div className="space-y-2">
          {pages.map((p) => (
            <div key={p.id} className="glass-card p-4 flex items-center gap-3" data-testid={`page-row-${p.slug}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-white font-bold truncate">{p.title}</span>
                  {p.published ? (
                    <span className="text-[10px] uppercase font-bold bg-green-500/20 text-green-400 px-2 py-0.5 rounded">Pubblicata</span>
                  ) : (
                    <span className="text-[10px] uppercase font-bold bg-white/10 text-white/60 px-2 py-0.5 rounded">Bozza</span>
                  )}
                  {p.show_in_menu && <span className="text-[10px] uppercase font-bold bg-pink-brand/20 text-pink-brand px-2 py-0.5 rounded">Menu</span>}
                </div>
                <div className="text-white/50 text-xs flex items-center gap-2">
                  <code className="text-pink-brand">/p/{p.slug}</code>
                  <span>· {p.blocks?.length || 0} blocchi</span>
                </div>
              </div>
              <a href={`/p/${p.slug}`} target="_blank" rel="noreferrer" className="text-white/60 hover:text-pink-brand p-2" title="Apri"><ExternalLink size={16} /></a>
              <button onClick={() => setEditing(p)} className="text-white/60 hover:text-pink-brand p-2" data-testid={`page-edit-${p.slug}`}><Edit3 size={16} /></button>
              <button onClick={() => remove(p.id)} className="text-white/60 hover:text-red-400 p-2" data-testid={`page-delete-${p.slug}`}><Trash2 size={16} /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PageEditor({ page, onSave, onCancel }) {
  const [data, setData] = useState({ ...page, blocks: page.blocks || [] });
  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const addBlock = (type) => {
    const def = BLOCK_TYPES.find((b) => b.type === type);
    setData((d) => ({ ...d, blocks: [...d.blocks, { id: uid(), type, ...(def?.defaults || {}) }] }));
  };
  const updateBlock = (i, patch) => {
    setData((d) => {
      const next = [...d.blocks]; next[i] = { ...next[i], ...patch };
      return { ...d, blocks: next };
    });
  };
  const removeBlock = (i) => setData((d) => ({ ...d, blocks: d.blocks.filter((_, idx) => idx !== i) }));
  const moveBlock = (i, dir) => {
    setData((d) => {
      const next = [...d.blocks];
      const j = i + dir;
      if (j < 0 || j >= next.length) return d;
      [next[i], next[j]] = [next[j], next[i]];
      return { ...d, blocks: next };
    });
  };

  return (
    <div className="space-y-4" data-testid="page-builder-editor">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <button onClick={onCancel} className="text-white/60 hover:text-white text-sm font-semibold mb-1 inline-flex items-center gap-1"><X size={14} /> Torna all'elenco</button>
          <h2 className="text-white font-bold text-lg">{data.id ? 'Modifica pagina' : 'Nuova pagina'}</h2>
        </div>
        <div className="flex gap-2">
          <button onClick={() => set('published', !data.published)} className="px-3 py-2 rounded-full bg-navy-dark border border-white/15 text-white/80 text-xs font-bold uppercase inline-flex items-center gap-1">
            {data.published ? <><Eye size={13} /> Pubblicata</> : <><EyeOff size={13} /> Bozza</>}
          </button>
          <button onClick={() => onSave(data)} className="btn-primary !py-2 !px-5 !text-sm" data-testid="page-save-button"><Save size={14} /> Salva</button>
        </div>
      </div>

      <div className="glass-card p-5 space-y-4">
        <Field label="Titolo">
          <input value={data.title} onChange={(e) => set('title', e.target.value)} className={inp} data-testid="page-title-input" />
        </Field>
        <Field label="Sottotitolo (opzionale)">
          <input value={data.subtitle || ''} onChange={(e) => set('subtitle', e.target.value)} className={inp} />
        </Field>
        <div className="grid sm:grid-cols-3 gap-4">
          <Field label="Slug URL">
            <div className="flex items-center bg-navy-dark border border-white/20 rounded-lg px-3 py-2">
              <span className="text-white/50 text-sm">/p/</span>
              <input value={data.slug || ''} onChange={(e) => set('slug', e.target.value)} placeholder="auto da titolo" className="flex-1 bg-transparent text-white text-sm focus:outline-none" data-testid="page-slug-input" />
            </div>
          </Field>
          <Field label="Mostra nel menu">
            <button type="button" onClick={() => set('show_in_menu', !data.show_in_menu)} className={`px-3 py-2 rounded-lg border text-xs font-bold uppercase ${data.show_in_menu ? 'bg-pink-brand text-white border-pink-brand' : 'bg-navy-dark text-white/70 border-white/20'}`}>
              {data.show_in_menu ? 'Sì, nel menu' : 'No, solo via URL'}
            </button>
          </Field>
          <Field label="Ordine menu">
            <input type="number" value={data.sort_order || 0} onChange={(e) => set('sort_order', parseInt(e.target.value, 10) || 0)} className={inp} />
          </Field>
        </div>
      </div>

      <div className="glass-card p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-white font-bold">Blocchi ({data.blocks.length})</h3>
        </div>
        <div className="flex flex-wrap gap-2 pb-4 border-b border-white/10 mb-4" data-testid="block-add-toolbar">
          {BLOCK_TYPES.map(({ type, label, icon: Icon }) => (
            <button key={type} onClick={() => addBlock(type)} className="px-3 py-2 rounded-full bg-navy-dark border border-white/15 text-white/80 hover:text-pink-brand hover:border-pink-brand text-xs font-bold uppercase inline-flex items-center gap-1.5" data-testid={`add-block-${type}`}>
              <Icon size={13} /> {label}
            </button>
          ))}
        </div>

        <div className="space-y-3">
          {data.blocks.map((b, i) => (
            <BlockCard key={b.id} index={i} total={data.blocks.length} block={b}
              onChange={(patch) => updateBlock(i, patch)}
              onRemove={() => removeBlock(i)}
              onMove={(dir) => moveBlock(i, dir)} />
          ))}
          {data.blocks.length === 0 && (
            <div className="text-center text-white/50 text-sm py-8">Aggiungi un blocco dalla barra qui sopra.</div>
          )}
        </div>
      </div>
    </div>
  );
}

const inp = 'w-full bg-navy-dark border border-white/20 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-pink-brand';

function Field({ label, children }) {
  return (
    <div>
      <label className="text-white/60 text-[10px] font-bold uppercase tracking-widest mb-1 block">{label}</label>
      {children}
    </div>
  );
}

function BlockCard({ index, total, block, onChange, onRemove, onMove }) {
  const meta = BLOCK_TYPES.find((b) => b.type === block.type);
  const Icon = meta?.icon || Type;
  return (
    <div className="bg-navy-dark border border-white/10 rounded-xl p-4" data-testid={`block-${block.type}-${index}`}>
      <div className="flex items-center gap-2 mb-3 pb-3 border-b border-white/10">
        <Icon size={16} className="text-pink-brand" />
        <span className="text-white font-bold text-sm uppercase tracking-widest">{meta?.label}</span>
        <span className="text-white/40 text-xs">#{index + 1}</span>
        <div className="ml-auto flex gap-1">
          <button onClick={() => onMove(-1)} disabled={index === 0} className="p-1.5 rounded text-white/60 hover:text-pink-brand disabled:opacity-30" data-testid={`block-up-${index}`}><ArrowUp size={14} /></button>
          <button onClick={() => onMove(1)} disabled={index === total - 1} className="p-1.5 rounded text-white/60 hover:text-pink-brand disabled:opacity-30" data-testid={`block-down-${index}`}><ArrowDown size={14} /></button>
          <button onClick={onRemove} className="p-1.5 rounded text-white/60 hover:text-red-400" data-testid={`block-remove-${index}`}><Trash2 size={14} /></button>
        </div>
      </div>
      <BlockFields block={block} onChange={onChange} />
    </div>
  );
}

function BlockFields({ block, onChange }) {
  switch (block.type) {
    case 'text':
      return (
        <Field label="Contenuto (markdown semplificato: **grassetto**, *corsivo*, [link](url), nuova riga vuota = paragrafo)">
          <textarea value={block.content || ''} onChange={(e) => onChange({ content: e.target.value })} rows={6} className={inp + ' font-mono text-xs'} />
        </Field>
      );
    case 'image':
      return (
        <div className="space-y-3">
          <UploadField label="Immagine (carica da PC o incolla URL)" accept="image/*" folder="pages" value={block.src} onChange={(v) => onChange({ src: v })} />
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Testo alternativo (alt)"><input value={block.alt || ''} onChange={(e) => onChange({ alt: e.target.value })} className={inp} /></Field>
            <Field label="Didascalia"><input value={block.caption || ''} onChange={(e) => onChange({ caption: e.target.value })} className={inp} /></Field>
          </div>
        </div>
      );
    case 'video':
      return (
        <div className="space-y-3">
          <UploadField label="Video (file MP4 da PC) oppure URL YouTube" accept="video/mp4" folder="pages" value={block.src} onChange={(v) => onChange({ src: v })} />
          <Field label="Titolo / didascalia (opzionale)"><input value={block.caption || ''} onChange={(e) => onChange({ caption: e.target.value })} className={inp} /></Field>
        </div>
      );
    case 'iframe':
      return (
        <div className="space-y-3">
          <Field label="URL embed (es. Google Maps, Facebook, ecc.)"><input value={block.src || ''} onChange={(e) => onChange({ src: e.target.value })} className={inp} placeholder="https://..." /></Field>
          <Field label="Altezza (px)"><input type="number" value={block.height || 480} onChange={(e) => onChange({ height: parseInt(e.target.value, 10) || 480 })} className={inp} /></Field>
        </div>
      );
    case 'banner':
      return (
        <div className="space-y-3">
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Titolo"><input value={block.title || ''} onChange={(e) => onChange({ title: e.target.value })} className={inp} /></Field>
            <Field label="Sottotitolo"><input value={block.subtitle || ''} onChange={(e) => onChange({ subtitle: e.target.value })} className={inp} /></Field>
          </div>
          <UploadField label="Immagine sfondo (opzionale)" accept="image/*" folder="banners" value={block.bg} onChange={(v) => onChange({ bg: v })} />
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Etichetta CTA"><input value={block.cta_label || ''} onChange={(e) => onChange({ cta_label: e.target.value })} className={inp} placeholder="Scopri di più" /></Field>
            <Field label="Link CTA"><input value={block.cta_url || ''} onChange={(e) => onChange({ cta_url: e.target.value })} className={inp} placeholder="/squadre o https://..." /></Field>
          </div>
        </div>
      );
    case 'gallery':
      return (
        <div className="space-y-3">
          <div className="text-white/60 text-xs">Carica immagini da PC: ognuna verrà aggiunta alla galleria. Puoi anche incollare URL nelle righe della casella sotto.</div>
          <UploadField
            label="Aggiungi immagine alla galleria"
            accept="image/*"
            folder="gallery"
            value=""
            onChange={(v) => { if (v) onChange({ items: [...(block.items || []), { src: v }] }); }}
          />
          <textarea
            rows={5}
            value={(block.items || []).map((i) => i.src).join('\n')}
            onChange={(e) => {
              const lines = e.target.value.split('\n').map((l) => l.trim()).filter(Boolean);
              onChange({ items: lines.map((src) => ({ src })) });
            }}
            className={inp + ' font-mono text-xs'}
            placeholder="URL immagini (uno per riga). Puoi rimuovere/riordinare manualmente da qui."
          />
          {block.items?.length > 0 && (
            <div className="grid grid-cols-4 gap-2">
              {block.items.map((it, idx) => (
                <div key={idx} className="relative group">
                  <img src={it.src} alt="" className="aspect-square object-cover rounded w-full" />
                  <button
                    type="button"
                    onClick={() => onChange({ items: block.items.filter((_, i) => i !== idx) })}
                    className="absolute top-1 right-1 w-6 h-6 rounded-full bg-red-500/90 text-white opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-xs"
                    title="Rimuovi"
                  >×</button>
                </div>
              ))}
            </div>
          )}
        </div>
      );
    default:
      return null;
  }
}
